import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tasksService } from 'src/data/api/helios-service/tasksService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  ListTasksRequest,
  SearchTasksRequest,
  Task,
  GetTaskRequest,
  GetTaskStatsRequest,
  GetTaskStatsResponse,
  AssigneeUpdateTaskRequest,
  AuditorUpdateTaskRequest,
  CancelTaskRequest,
  CreateTaskRequest,
  CreateTaskFromQuestionRequest,
  ReopenTaskRequest,
} from 'src/types/helios/Task';
import { handleError } from 'src/utils/generalUtils';

export interface TaskState {
  tasks: Task[];
  selectedTask: Task | null;
  taskStats?: GetTaskStatsResponse;
  fetchTasksApiStatus: ApiRequestStatus & { nextToken?: string };
  searchTasksApiStatus: ApiRequestStatus & { nextToken?: string };
  getTaskApiStatus: ApiRequestStatus;
  taskStatsApiStatus: ApiRequestStatus;
  updateAssigneeApiStatus: ApiRequestStatus;
  auditorUpdateApiStatus: ApiRequestStatus;
  cancelTaskApiStatus: ApiRequestStatus;
  reopenTaskApiStatus: ApiRequestStatus;
  createTaskApiStatus: ApiRequestStatus & { createdTaskId?: string };
  createTaskFromQuestionApiStatus: ApiRequestStatus & { createdTaskId?: string };
}

const initialState: TaskState = {
  tasks: [],
  selectedTask: null,
  fetchTasksApiStatus: {
    ...initApiStatus,
  },
  searchTasksApiStatus: {
    ...initApiStatus,
  },
  getTaskApiStatus: initApiStatus,
  taskStatsApiStatus: initApiStatus,
  updateAssigneeApiStatus: initApiStatus,
  auditorUpdateApiStatus: initApiStatus,
  cancelTaskApiStatus: initApiStatus,
  reopenTaskApiStatus: initApiStatus,
  createTaskApiStatus: initApiStatus,
  createTaskFromQuestionApiStatus: initApiStatus,
};

export const fetchTasksApiThunk = createAsyncThunk(
  'tasks/fetchTasks',
  async (data: ListTasksRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.list(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const searchTasksApiThunk = createAsyncThunk(
  'tasks/searchTasks',
  async (data: SearchTasksRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.search(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getTaskApiThunk = createAsyncThunk(
  'tasks/getTask',
  async (data: GetTaskRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.get(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateAssigneeApiThunk = createAsyncThunk(
  'tasks/updateAssignee',
  async (data: AssigneeUpdateTaskRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.updateAssignee(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const auditorUpdateApiThunk = createAsyncThunk(
  'tasks/auditorUpdate',
  async (data: AuditorUpdateTaskRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.auditorUpdate(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const cancelTaskApiThunk = createAsyncThunk(
  'tasks/cancelTask',
  async (data: CancelTaskRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.cancel(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createTaskApiThunk = createAsyncThunk(
  'tasks/createTask',
  async (data: CreateTaskRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.create(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createTaskFromQuestionApiThunk = createAsyncThunk(
  'tasks/createTaskFromQuestion',
  async (data: CreateTaskFromQuestionRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.createFromQuestion(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getTaskStatsApiThunk = createAsyncThunk(
  'tasks/getTaskStats',
  async (data: GetTaskStatsRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.getStats(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const reopenTaskApiThunk = createAsyncThunk(
  'tasks/reopenTask',
  async (data: ReopenTaskRequest, { rejectWithValue }) => {
    try {
      const response = await tasksService.reopen(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    resetAllApiStatus: (state) => {
      state.fetchTasksApiStatus = initApiStatus;
      state.searchTasksApiStatus = initApiStatus;
      state.getTaskApiStatus = initApiStatus;
      state.taskStatsApiStatus = initApiStatus;
      state.updateAssigneeApiStatus = initApiStatus;
      state.auditorUpdateApiStatus = initApiStatus;
      state.cancelTaskApiStatus = initApiStatus;
      state.reopenTaskApiStatus = initApiStatus;
      state.createTaskApiStatus = initApiStatus;
      state.createTaskFromQuestionApiStatus = initApiStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasksApiThunk.pending, (state) => {
        state.fetchTasksApiStatus.isLoading = true;
        state.fetchTasksApiStatus.errorMessage = null;
      })
      .addCase(fetchTasksApiThunk.fulfilled, (state, action) => {
        state.fetchTasksApiStatus.isLoading = false;
        state.fetchTasksApiStatus.isSuccess = true;
        state.fetchTasksApiStatus.nextToken = action.payload.nextToken;
        state.tasks = action.payload.tasks;
      })
      .addCase(fetchTasksApiThunk.rejected, (state, action) => {
        state.fetchTasksApiStatus.isLoading = false;
        state.fetchTasksApiStatus.isSuccess = false;
        state.fetchTasksApiStatus.errorMessage = action.payload as string;
      })
      // Search Tasks cases
      .addCase(searchTasksApiThunk.pending, (state) => {
        state.searchTasksApiStatus.isLoading = true;
        state.searchTasksApiStatus.errorMessage = null;
      })
      .addCase(searchTasksApiThunk.fulfilled, (state, action) => {
        state.searchTasksApiStatus.isLoading = false;
        state.searchTasksApiStatus.isSuccess = true;
        state.searchTasksApiStatus.nextToken = action.payload.nextToken;
        state.tasks = action.payload.tasks;
      })
      .addCase(searchTasksApiThunk.rejected, (state, action) => {
        state.searchTasksApiStatus.isLoading = false;
        state.searchTasksApiStatus.isSuccess = false;
        state.searchTasksApiStatus.errorMessage = action.payload as string;
      })
      // Get Task cases
      .addCase(getTaskApiThunk.pending, (state) => {
        state.getTaskApiStatus.isLoading = true;
        state.getTaskApiStatus.errorMessage = null;
      })
      .addCase(getTaskApiThunk.fulfilled, (state, action) => {
        state.getTaskApiStatus.isLoading = false;
        state.getTaskApiStatus.isSuccess = true;
        state.selectedTask = action.payload.task;
      })
      .addCase(getTaskApiThunk.rejected, (state, action) => {
        state.getTaskApiStatus.isLoading = false;
        state.getTaskApiStatus.isSuccess = false;
        state.getTaskApiStatus.errorMessage = action.payload as string;
      })
      // Update Assignee cases
      .addCase(updateAssigneeApiThunk.pending, (state) => {
        state.updateAssigneeApiStatus.isLoading = true;
        state.updateAssigneeApiStatus.errorMessage = null;
        state.updateAssigneeApiStatus.successMessage = null;
      })
      .addCase(updateAssigneeApiThunk.fulfilled, (state, action) => {
        state.updateAssigneeApiStatus.isLoading = false;
        state.updateAssigneeApiStatus.isSuccess = true;
        state.updateAssigneeApiStatus.successMessage = `Task Reassigned to ${action.payload.task.assigneeLogin}`;
        state.selectedTask = action.payload.task;
      })
      .addCase(updateAssigneeApiThunk.rejected, (state, action) => {
        state.updateAssigneeApiStatus.isLoading = false;
        state.updateAssigneeApiStatus.isSuccess = false;
        state.updateAssigneeApiStatus.errorMessage = action.payload as string;
      })
      // Auditor Update cases
      .addCase(auditorUpdateApiThunk.pending, (state) => {
        state.auditorUpdateApiStatus.isLoading = true;
        state.auditorUpdateApiStatus.errorMessage = null;
        state.auditorUpdateApiStatus.successMessage = null;
      })
      .addCase(auditorUpdateApiThunk.fulfilled, (state, action) => {
        state.auditorUpdateApiStatus.isLoading = false;
        state.auditorUpdateApiStatus.isSuccess = true;
        // Only show success message when task status is COMPLETED (after user confirmation)
        const taskStatus = action.payload.task.taskStatus;
        state.auditorUpdateApiStatus.successMessage =
          taskStatus === 'COMPLETED' ? 'Task updated successfully' : null;
        state.selectedTask = action.payload.task;
      })
      .addCase(auditorUpdateApiThunk.rejected, (state, action) => {
        state.auditorUpdateApiStatus.isLoading = false;
        state.auditorUpdateApiStatus.isSuccess = false;
        state.auditorUpdateApiStatus.errorMessage = action.payload as string;
      })
      // Cancel Task cases
      .addCase(cancelTaskApiThunk.pending, (state) => {
        state.cancelTaskApiStatus.isLoading = true;
        state.cancelTaskApiStatus.errorMessage = null;
        state.cancelTaskApiStatus.successMessage = null;
      })
      .addCase(cancelTaskApiThunk.fulfilled, (state, action) => {
        state.cancelTaskApiStatus.isLoading = false;
        state.cancelTaskApiStatus.isSuccess = true;
        state.cancelTaskApiStatus.successMessage = 'Task cancelled successfully';
      })
      .addCase(cancelTaskApiThunk.rejected, (state, action) => {
        state.cancelTaskApiStatus.isLoading = false;
        state.cancelTaskApiStatus.isSuccess = false;
        state.cancelTaskApiStatus.errorMessage = action.payload as string;
      })
      // Create Task cases
      .addCase(createTaskApiThunk.pending, (state) => {
        state.createTaskApiStatus.isLoading = true;
        state.createTaskApiStatus.errorMessage = null;
        state.createTaskApiStatus.successMessage = null;
      })
      .addCase(createTaskApiThunk.fulfilled, (state, action) => {
        state.createTaskApiStatus.isLoading = false;
        state.createTaskApiStatus.isSuccess = true;
        state.createTaskApiStatus.createdTaskId = action.payload.taskID;
        state.createTaskApiStatus.successMessage = `Task ${action.payload.taskID} generated successfully`;
      })
      .addCase(createTaskApiThunk.rejected, (state, action) => {
        state.createTaskApiStatus.isLoading = false;
        state.createTaskApiStatus.isSuccess = false;
        state.createTaskApiStatus.errorMessage = action.payload as string;
      })
      // Create Task From Question cases
      .addCase(createTaskFromQuestionApiThunk.pending, (state) => {
        state.createTaskFromQuestionApiStatus.isLoading = true;
        state.createTaskFromQuestionApiStatus.errorMessage = null;
        state.createTaskFromQuestionApiStatus.successMessage = null;
      })
      .addCase(createTaskFromQuestionApiThunk.fulfilled, (state, action) => {
        state.createTaskFromQuestionApiStatus.isLoading = false;
        state.createTaskFromQuestionApiStatus.isSuccess = true;
        state.createTaskFromQuestionApiStatus.createdTaskId = action.payload.taskId;
        state.createTaskFromQuestionApiStatus.successMessage = `Task ${action.payload.taskId} generated successfully`;
      })
      .addCase(createTaskFromQuestionApiThunk.rejected, (state, action) => {
        state.createTaskFromQuestionApiStatus.isLoading = false;
        state.createTaskFromQuestionApiStatus.isSuccess = false;
        state.createTaskFromQuestionApiStatus.errorMessage = action.payload as string;
      })
      // Get Task Stats cases
      .addCase(getTaskStatsApiThunk.pending, (state) => {
        state.taskStatsApiStatus.isLoading = true;
        state.taskStatsApiStatus.errorMessage = null;
      })
      .addCase(getTaskStatsApiThunk.fulfilled, (state, action) => {
        state.taskStatsApiStatus.isLoading = false;
        state.taskStatsApiStatus.isSuccess = true;
        state.taskStats = action.payload;
      })
      .addCase(getTaskStatsApiThunk.rejected, (state, action) => {
        state.taskStatsApiStatus.isLoading = false;
        state.taskStatsApiStatus.isSuccess = false;
        state.taskStatsApiStatus.errorMessage = action.payload as string;
      })
      // Reopen Task cases
      .addCase(reopenTaskApiThunk.pending, (state) => {
        state.reopenTaskApiStatus.isLoading = true;
        state.reopenTaskApiStatus.errorMessage = null;
        state.reopenTaskApiStatus.successMessage = null;
      })
      .addCase(reopenTaskApiThunk.fulfilled, (state, action) => {
        state.reopenTaskApiStatus.isLoading = false;
        state.reopenTaskApiStatus.isSuccess = true;
        state.reopenTaskApiStatus.successMessage = 'Task reopened successfully';
        state.selectedTask = action.payload.task;
      })
      .addCase(reopenTaskApiThunk.rejected, (state, action) => {
        state.reopenTaskApiStatus.isLoading = false;
        state.reopenTaskApiStatus.isSuccess = false;
        state.reopenTaskApiStatus.errorMessage = action.payload as string;
      });
  },
});

export const { resetAllApiStatus } = tasksSlice.actions;
export default tasksSlice.reducer;
